import { useCallback, useEffect, useRef, useState } from 'react';

import type { AiChatAttachmentRef, AiChatElicitationAnswer, AiChatMessage, AiChatModelOption, AiChatRepository } from '@/domain/repositories/ai-chat-repository.js';
import type { EfChatMessageView } from '@/presentation/pages/developments/chat/ef-chat-message.js';

export type EfChatElicitationOption = { id: string; label: string };

export type EfChatElicitationQuestion = {
    id: string;
    prompt: string;
    options: EfChatElicitationOption[];
    allowMultiple: boolean;
    allowFreeText: boolean;
};

export type EfChatElicitation = {
    toolCallId: string;
    questions: EfChatElicitationQuestion[];
};

type ElicitationQuestionPayload = {
    id?: string;
    prompt?: string;
    options?: EfChatElicitationOption[];
    allowMultiple?: boolean;
    allowFreeText?: boolean;
};

type ElicitationPayload = {
    toolCallId?: string;
    prompt?: string;
    options?: EfChatElicitationOption[];
    allowMultiple?: boolean;
    allowFreeText?: boolean;
    questions?: ElicitationQuestionPayload[];
};

const normalizeElicitationQuestions = (payload: ElicitationPayload): EfChatElicitationQuestion[] => {
    if (payload.questions && payload.questions.length > 0) {
        return payload.questions.map((question, index) => {
            const options = question.options ?? [];
            return {
                id: question.id ?? `q${index + 1}`,
                prompt: question.prompt ?? '',
                options,
                allowMultiple: question.allowMultiple ?? false,
                allowFreeText: question.allowFreeText ?? options.length === 0
            };
        });
    }
    const options = payload.options ?? [];
    return [
        {
            id: 'q1',
            prompt: payload.prompt ?? '',
            options,
            allowMultiple: payload.allowMultiple ?? false,
            allowFreeText: payload.allowFreeText ?? true
        }
    ];
};

export type UseEfChatOptions = {
    onVerdict?: () => void;
};

export type EfChatContextUsage = {
    used: number;
    limit: number;
};

const toView = (message: AiChatMessage): EfChatMessageView & { createdAt: string } => ({
    id: message.id,
    role: message.role,
    content: message.content,
    attachments: message.attachments,
    reasoning: '',
    status: message.status,
    createdAt: typeof message.createdAt === 'string' ? message.createdAt : new Date(message.createdAt).toISOString()
});

type DeltaPayload = {
    messageId?: string;
    delta?: string;
    part?: 'text' | 'thinking';
};

type StatusPayload = {
    status?: string;
    error?: string | null;
};

export type UseEfChatResult = {
    ready: boolean;
    error: string | null;
    canSend: boolean;
    isStreaming: boolean;
    messages: Array<EfChatMessageView & { createdAt: string }>;
    models: AiChatModelOption[];
    model: string;
    setModel: (modelId: string) => void;
    send: (content: string, attachments?: AiChatAttachmentRef[]) => Promise<void>;
    startAnalysis: (instruction?: string, attachments?: AiChatAttachmentRef[]) => Promise<void>;
    cancel: () => Promise<void>;
    elicitation: EfChatElicitation | null;
    resolveElicitation: (answer: AiChatElicitationAnswer) => Promise<void>;
    hasAnalysis: boolean;
    contextUsage: EfChatContextUsage | null;
};

export function useEfChat(developmentId: string, repository: AiChatRepository, options: UseEfChatOptions = {}): UseEfChatResult {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [canSend, setCanSend] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [messages, setMessages] = useState<Array<EfChatMessageView & { createdAt: string }>>([]);
    const [models, setModels] = useState<AiChatModelOption[]>([]);
    const [model, setModel] = useState('');
    const [elicitation, setElicitation] = useState<EfChatElicitation | null>(null);
    const [contextUsage, setContextUsage] = useState<EfChatContextUsage | null>(null);
    const streamingIdRef = useRef<string | null>(null);
    const canceledRef = useRef(false);
    const modelLimitRef = useRef<number | null>(null);
    modelLimitRef.current = models.find((option) => option.id === model)?.contextLength ?? null;
    const onVerdictRef = useRef<(() => void) | undefined>(options.onVerdict);
    onVerdictRef.current = options.onVerdict;

    const applyDelta = useCallback((payload: DeltaPayload) => {
        if (canceledRef.current) {
            return;
        }
        const part = payload.part ?? 'text';
        if (!payload.messageId || (part !== 'text' && part !== 'thinking') || typeof payload.delta !== 'string' || payload.delta.length === 0) {
            return;
        }
        const messageId = payload.messageId;
        const delta = payload.delta;
        streamingIdRef.current = messageId;
        setIsStreaming(true);
        setMessages((current) => {
            const index = current.findIndex((message) => message.id === messageId);
            if (index >= 0) {
                const next = [...current];
                const existing = next[index];
                if (!existing) {
                    return current;
                }
                next[index] = {
                    ...existing,
                    content: part === 'text' ? `${existing.content}${delta}` : existing.content,
                    reasoning: part === 'thinking' ? `${existing.reasoning ?? ''}${delta}` : existing.reasoning,
                    status: 'streaming'
                };
                return next;
            }
            return [
                ...current,
                {
                    id: messageId,
                    role: 'assistant',
                    content: part === 'text' ? delta : '',
                    reasoning: part === 'thinking' ? delta : '',
                    status: 'streaming',
                    createdAt: new Date().toISOString()
                }
            ];
        });
    }, []);

    const finalizeStreaming = useCallback((status: 'complete' | 'error', errorMessage?: string) => {
        const streamingId = streamingIdRef.current;
        streamingIdRef.current = null;
        setIsStreaming(false);
        if (!streamingId) {
            return;
        }
        setMessages((current) =>
            current.map((message) => {
                if (message.id !== streamingId) {
                    return message;
                }
                const resolvedContent = status === 'error' ? (errorMessage ?? '') : message.content;
                return { ...message, status, content: resolvedContent };
            })
        );
    }, []);

    useEffect(() => {
        let closed = false;
        let subscription: { close: () => void } | null = null;

        const bootstrap = async (): Promise<void> => {
            try {
                const grant = await repository.mintGrant(developmentId);
                if (closed) {
                    return;
                }
                setCanSend(grant.canSend);
                const [history, catalog] = await Promise.all([repository.getHistory(developmentId), repository.listModels()]);
                if (closed) {
                    return;
                }
                setMessages(history.messages.map(toView));
                setModels(catalog.models);
                setModel(catalog.defaultModelId);
                if (history.contextUsage && typeof history.contextUsage.used === 'number') {
                    const limit = typeof history.contextUsage.limit === 'number' && history.contextUsage.limit > 0 ? history.contextUsage.limit : modelLimitRef.current;
                    if (typeof limit === 'number' && limit > 0) {
                        setContextUsage({ used: history.contextUsage.used, limit });
                    }
                }
                subscription = repository.openEvents(developmentId, {
                    onEvent: (type, data) => {
                        if (type === 'message.delta') {
                            applyDelta(data as DeltaPayload);
                            return;
                        }
                        if (type === 'tool.elicitation') {
                            const payload = data as ElicitationPayload;
                            if (payload.toolCallId) {
                                setElicitation({
                                    toolCallId: payload.toolCallId,
                                    questions: normalizeElicitationQuestions(payload)
                                });
                            }
                            return;
                        }
                        if (type === 'context.usage') {
                            const payload = data as { used?: number; limit?: number };
                            const used = typeof payload.used === 'number' ? payload.used : null;
                            const limit = typeof payload.limit === 'number' && payload.limit > 0 ? payload.limit : modelLimitRef.current;
                            if (used !== null && typeof limit === 'number' && limit > 0) {
                                setContextUsage({ used, limit });
                            }
                            return;
                        }
                        if (type === 'analysis.verdict') {
                            setElicitation(null);
                            onVerdictRef.current?.();
                            return;
                        }
                        if (type === 'analysis.error') {
                            const payload = data as { message?: string };
                            const messageText = payload.message;
                            setElicitation(null);
                            finalizeStreaming('complete');
                            if (messageText) {
                                setMessages((current) => [
                                    ...current,
                                    {
                                        id: `local-error-${Date.now()}`,
                                        role: 'assistant',
                                        content: messageText,
                                        reasoning: '',
                                        status: 'error',
                                        createdAt: new Date().toISOString()
                                    }
                                ]);
                            }
                            onVerdictRef.current?.();
                            return;
                        }
                        if (type === 'session.status') {
                            const payload = data as StatusPayload;
                            if (payload.status === 'error') {
                                finalizeStreaming('error', payload.error ?? '');
                                return;
                            }
                            if (payload.status === 'doom_loop' || payload.status === 'budget_reached') {
                                finalizeStreaming('complete');
                                onVerdictRef.current?.();
                                return;
                            }
                            if (payload.status === 'idle' || payload.status === 'completed' || payload.status === 'ready') {
                                finalizeStreaming('complete');
                            }
                        }
                    }
                });
                setReady(true);
                setError(null);
            } catch (err) {
                if (!closed) {
                    setError(err instanceof Error ? err.message : 'chat bootstrap failed');
                    setReady(false);
                }
            }
        };

        void bootstrap();

        return () => {
            closed = true;
            subscription?.close();
        };
    }, [applyDelta, developmentId, finalizeStreaming, repository]);

    const send = useCallback(
        async (content: string, attachments?: AiChatAttachmentRef[]): Promise<void> => {
            const trimmed = content.trim();
            const hasAttachments = Boolean(attachments && attachments.length > 0);
            if (!canSend || isStreaming) {
                return;
            }
            if (!trimmed && !hasAttachments) {
                return;
            }
            const optimisticId = `local-user-${Date.now()}`;
            setMessages((current) => [
                ...current,
                {
                    id: optimisticId,
                    role: 'user',
                    content: trimmed,
                    attachments,
                    status: 'complete',
                    createdAt: new Date().toISOString()
                }
            ]);
            canceledRef.current = false;
            setIsStreaming(true);
            try {
                await repository.sendMessage(developmentId, trimmed, attachments, model || undefined);
            } catch (err) {
                setIsStreaming(false);
                setError(err instanceof Error ? err.message : 'send failed');
                setMessages((current) => current.filter((message) => message.id !== optimisticId));
                throw err;
            }
        },
        [canSend, developmentId, isStreaming, model, repository]
    );

    const startAnalysis = useCallback(
        async (instruction?: string, attachments?: AiChatAttachmentRef[]): Promise<void> => {
            if (isStreaming) {
                return;
            }
            canceledRef.current = false;
            const trimmed = instruction?.trim();
            const hasAttachments = Boolean(attachments && attachments.length > 0);
            if (trimmed || hasAttachments) {
                setMessages((current) => [
                    ...current,
                    {
                        id: `local-user-${Date.now()}`,
                        role: 'user',
                        content: trimmed ?? '',
                        attachments,
                        status: 'complete',
                        createdAt: new Date().toISOString()
                    }
                ]);
            }
            setIsStreaming(true);
            setError(null);
            try {
                await repository.startAnalysis(developmentId, trimmed || undefined, attachments);
            } catch (err) {
                setIsStreaming(false);
                setError(err instanceof Error ? err.message : 'start analysis failed');
            }
        },
        [developmentId, isStreaming, repository]
    );

    const cancel = useCallback(async (): Promise<void> => {
        setError(null);
        canceledRef.current = true;
        setElicitation(null);
        finalizeStreaming('complete');
        try {
            await repository.cancelAnalysis(developmentId);
            onVerdictRef.current?.();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'cancel failed');
        }
    }, [developmentId, finalizeStreaming, repository]);

    const resolveElicitation = useCallback(
        async (answer: AiChatElicitationAnswer): Promise<void> => {
            const pending = elicitation;
            if (!pending) {
                return;
            }
            setElicitation(null);
            try {
                await repository.resolveElicitation(developmentId, pending.toolCallId, answer);
            } catch (err) {
                setElicitation(pending);
                setError(err instanceof Error ? err.message : 'resolve elicitation failed');
            }
        },
        [developmentId, elicitation, repository]
    );

    return {
        ready,
        error,
        canSend,
        isStreaming,
        messages,
        models,
        model,
        setModel,
        send,
        startAnalysis,
        cancel,
        elicitation,
        resolveElicitation,
        hasAnalysis: messages.length > 0,
        contextUsage
    };
}
